import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  User, Heart, Flag, Users, Phone, Trophy, Target
} from 'lucide-react';
import { format } from 'date-fns';
import { ComprehensiveUserProfile } from './types';

interface ProfileTabProps {
  profile: ComprehensiveUserProfile;
}

export const ProfileTab: React.FC<ProfileTabProps> = ({ profile }) => {
  const { user, focusOlderAdult, hobbies, milestones, goals, emergencyContacts, circleMemberships } = profile;

  return (
    <div className="space-y-6">
      {/* Extended Profile Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              Extended Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {user?.preferredName && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Preferred Name</span>
                <span>{user.preferredName}</span>
              </div>
            )}
            {(user?.streetAddress || user?.homeAddress) && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Address</span>
                <span>{user.streetAddress || user.homeAddress}</span>
              </div>
            )}
            {user?.homeType && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Home Type</span>
                <span className="capitalize">{user.homeType}</span>
              </div>
            )}
            {user?.homeLevels && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Home Levels</span>
                <span>{user.homeLevels}</span>
              </div>
            )}
            {user?.about && (
              <div className="pt-2 border-t border-border">
                <p className="text-muted-foreground text-xs mb-1">About</p>
                <p className="text-sm">{user.about}</p>
              </div>
            )}
            {focusOlderAdult?.userSnapshotAI && (
              <div className="pt-2 border-t border-border">
                <p className="text-muted-foreground text-xs mb-1">AI Snapshot</p>
                <p className="text-sm line-clamp-4">{focusOlderAdult.userSnapshotAI}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Circle Memberships */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              ThriveCircle Memberships ({circleMemberships?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {circleMemberships?.length > 0 ? (
              <div className="space-y-2">
                {circleMemberships.map((membership) => (
                  <div key={membership.id} className="p-3 rounded-lg bg-muted/50 border border-border/50">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium text-sm">{membership.circleName}</span>
                      <Badge variant={membership.circleStatus === 'ACTIVE' ? 'default' : 'secondary'} className="text-xs">
                        {membership.circleStatus}
                      </Badge>
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{membership.role?.replace(/_/g, ' ')}</span>
                      <span>Joined {membership.createdAt ? format(new Date(membership.createdAt), 'MMM yyyy') : ''}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No circle memberships</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Hobbies and Emergency Contacts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Hobbies */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Heart className="h-4 w-4 text-pink-500" />
              Hobbies & Interests
            </CardTitle>
          </CardHeader>
          <CardContent>
            {hobbies?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {hobbies.map((hobby, idx) => (
                  <Badge 
                    key={idx} 
                    variant={hobby.isFavorite ? 'default' : 'outline'}
                    className="flex items-center gap-1"
                  >
                    {hobby.isFavorite && <Heart className="h-3 w-3 fill-current" />}
                    {hobby.name}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No hobbies recorded</p>
            )}
          </CardContent>
        </Card>

        {/* Emergency Contacts */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Phone className="h-4 w-4 text-red-500" />
              Emergency Contacts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {emergencyContacts?.length > 0 ? (
              <div className="space-y-2">
                {emergencyContacts.map((contact, idx) => (
                  <div key={idx} className="p-3 rounded-lg bg-muted/50 border border-border/50">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium text-sm">{contact.name}</span>
                      {contact.isPrimary && (
                        <Badge variant="default" className="text-xs">Primary</Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <p>{contact.relationship}</p>
                      <p>{contact.phone}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No emergency contacts</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Milestones and Goals */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Milestones */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Trophy className="h-4 w-4 text-yellow-500" />
              Milestones Achieved ({milestones?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {milestones?.length > 0 ? (
              <div className="space-y-2">
                {milestones.slice(0, 10).map((milestone, idx) => (
                  <div key={idx} className="p-2 rounded bg-muted/50 border border-border/50 flex justify-between items-center">
                    <span className="text-sm">{milestone.type?.replace(/_/g, ' ')}</span>
                    <span className="text-xs text-muted-foreground">
                      {milestone.achievedAt ? format(new Date(milestone.achievedAt), 'MMM d, yyyy') : ''}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No milestones achieved</p>
            )}
          </CardContent>
        </Card>

        {/* Goals */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4 text-green-500" />
              Goals ({goals?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {goals?.length > 0 ? (
              <div className="space-y-3">
                {goals.slice(0, 5).map((goal) => (
                  <div key={goal.id} className="p-3 rounded-lg bg-muted/50 border border-border/50">
                    <p className="font-medium text-sm mb-1">{goal.title}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{goal.description}</p>
                    <div className="flex flex-wrap gap-1">
                      {goal.categories?.map((cat, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">{cat}</Badge>
                      ))}
                    </div>
                    {goal.dueDate && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Due: {format(new Date(goal.dueDate), 'MMM d, yyyy')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No goals set</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
